import React, { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import DraggableModal from 'src/app/components/DraggableModal/DraggableModal';
import { AppstoreAddOutlined, DeleteOutlined, MinusOutlined, PlusOutlined, SelectOutlined } from '@ant-design/icons';
import { drawenLayerService } from 'src/app/services';
import SearchableSelect from 'src/app/components/SearchableSelect/SearchableSelect';
import { statusHelper } from 'src/app/helpers';
import styles from './RotateModal.module.scss';
import { RotateModalPropsType } from './rotate-modal-config';

import Input from 'antd/es/input';
import Form from 'antd/es/form';
import Popconfirm from 'antd/es/popconfirm';
import Tooltip from 'antd/es/tooltip';
import Button from 'antd/es/button/button';
import Slider from 'antd/es/slider';
import Select from 'antd/es/select';
import Switch from 'antd/es/switch';

enum ArrowType {
  NONE = 'none',
  ARROW_HEADS = 'arrowHeads',
  START_END_ARROW = 'startEndArrow',
}

enum ArrowFillType {
  DISABLED = 'disabled',
  NONE = 'none',
  FILL = 'fill',
}

const RotateModal = ({ onClose, feature, map, drawController }: RotateModalPropsType) => {
  const [form] = Form.useForm();
  const [featureType, setFeatureType] = useState<string | null>(null);
  const [coordinates, setCoordinates] = useState<number[][]>([]);
  const [rotation, setRotation] = useState<number>(0);
  const [isCircleWithRadius, setIsCircleWithRadius] = useState(false);
  const [circleRadius, setCircleRadius] = useState<number | null>(null);
  const [radiusStep, setRadiusStep] = useState<number>(1000);
  const [inputKm, setInputKm] = useState('');
  const [isEditingKm, setIsEditingKm] = useState(false);
  const [groupName, setGroupName] = useState<string | null>(null);

  const [isDashed, setIsDashed] = useState(false);
  const [arrowType, setArrowType] = useState<ArrowType>(ArrowType.NONE);
  const [arrowFillType, setArrowFillType] = useState<ArrowFillType>(ArrowFillType.NONE);
  const [shapeColor, setShapeColor] = useState<string>('#000000');
  const [shapeBorderColor, setShapeBorderColor] = useState<string>('#000000');
  const [shapeOpacity, setShapeOpacity] = useState<number>(0.2);
  const [opacityInput, setOpacityInput] = useState<string>('20');

  const [inputs, setInputs] = useState<Record<number, { lat: string; lng: string }>>({});
  const correctionTimeoutsRef = useRef<Record<string, NodeJS.Timeout>>({});

  // preview layers and markers
  const previewLayerRef = useRef<L.GeoJSON | null>(null);
  const [lastPointMarker, setLastPointMarker] = useState<L.Marker | null>(null);
  const [tempCircleLayer, setTempCircleLayer] = useState<L.Circle | L.Marker | null>(null);
  const [startPointMarker, setStartPointMarker] = useState<L.Marker | L.CircleMarker | null>(null);
  const [isSelecting, setIsSelecting] = useState(false);
  const [isCircleMarker, setIsCircleMarker] = useState(false);
  const [shapeName, setShapeName] = useState<string>(feature.properties?.name || '');
  const [isMarker, setIsMarker] = useState(false);
  const [isText, setIsText] = useState(false);
  const [textFontSize, setTextFontSize] = useState<number>(18);
  // feature center point for editing
  const centerRef = useRef<{ lng: number; lat: number } | null>(null);

  const rotatePoint = (
    lng: number,
    lat: number,
    centerLng: number,
    centerLat: number,
    angleRad: number,
  ): [number, number] => {
    const dx = lng - centerLng;
    const dy = lat - centerLat;
    const newLng = centerLng + dx * Math.cos(angleRad) + dy * Math.sin(angleRad);
    const newLat = centerLat - dx * Math.sin(angleRad) + dy * Math.cos(angleRad);
    return [newLng, newLat];
  };

  const inverseRotatePoint = (
    lng: number,
    lat: number,
    centerLng: number,
    centerLat: number,
    angleRad: number,
  ): [number, number] => {
    const dx = lng - centerLng;
    const dy = lat - centerLat;
    const origLng = centerLng + dx * Math.cos(angleRad) - dy * Math.sin(angleRad);
    const origLat = centerLat + dx * Math.sin(angleRad) + dy * Math.cos(angleRad);
    return [origLng, origLat];
  };

  // find related group for the feature mapping drawcontorller.drawgroups
  const findGroup = () => {
    drawController?.drawGroups.forEach((g: { text: string; drawenLayers: { uuid: string }[] }) => {
      const layer = g.drawenLayers.find((l: { uuid: string }) => l.uuid === feature.properties?.uuid);
      if (layer) {
        setGroupName(g.text);
      }
    });
  };
  useEffect(() => {
    findGroup();
  }, [feature, drawController]);

  const calculateCenter = (coords: number[][] | number[][][]): { lng: number; lat: number } => {
    if (!coords || coords.length === 0) {
      return { lng: 0, lat: 0 };
    }

    // for Polygon take the first ring, otherwise take the line
    const raw: number[][] = Array.isArray((coords as any)[0][0]) ? (coords as number[][][])[0] : (coords as number[][]);

    // map into [lat, lng] and drop any invalid ones
    const latlngs: [number, number][] = raw
      .map(([lng, lat]) => [lat, lng] as [number, number])
      .filter(([lat, lng]) => !Number.isNaN(lat) && !Number.isNaN(lng));

    if (latlngs.length === 0) {
      return { lng: 0, lat: 0 };
    }

    const center = L.latLngBounds(latlngs).getCenter();
    return { lng: center.lng, lat: center.lat };
  };

  const formatKm = (km: number) => km.toFixed(3).replace('.', ',').replace(/,?0+$/, '');

  useEffect(() => {
    if (circleRadius != null && !isEditingKm) {
      setInputKm(formatKm(circleRadius / 1000));
    }
  }, [circleRadius, isEditingKm]);

  const initializeState = () => {
    if (!feature) return;
    const geom = feature.geometry;

    const circleMarkerMode = geom.type === 'Point' && feature.properties?.radius === 10;
    const markerMode = geom.type === 'Point' && !feature.properties?.radius;
    const textMode = feature.properties?.isLabel || false;

    setIsCircleMarker(circleMarkerMode);
    setIsMarker(markerMode);
    if (textMode) {
      setIsMarker(false);
      setIsText(true);
    }
    setShapeName(feature.properties?.name || '');
    setTextFontSize(feature.properties?.fontSize || 18);
    setFeatureType(geom.type);

    // set coordinate array for LineString/Polygon editing
    if (geom.type === 'LineString') {
      setCoordinates([...(geom.coordinates as number[][])]);
    } else if (geom.type === 'Polygon') {
      setCoordinates([...(geom.coordinates as number[][][])[0]]);
    } else {
      setCoordinates([]);
    }

    // circle with radius
    if (geom.type === 'Point' && typeof feature.properties?.radius === 'number') {
      const r = feature.properties.radius;
      setIsCircleWithRadius(r !== 10);
      setCircleRadius(r);
      setInputKm(formatKm(r / 1000));
    } else {
      setIsCircleWithRadius(false);
      setCircleRadius(null);
      setInputKm('');
    }

    // style defaults
    const style = feature.properties?.style || {};
    setIsDashed(Boolean(style.dashArray));
    setArrowType(
      feature.properties?.arrowHead
        ? ArrowType.ARROW_HEADS
        : feature.properties?.startEndArrow
        ? ArrowType.START_END_ARROW
        : ArrowType.NONE,
    );
    setArrowFillType(
      feature.properties?.arrowFillType === ArrowFillType.FILL ? ArrowFillType.FILL : ArrowFillType.NONE,
    );
    setShapeColor(style.fillColor ?? style.color ?? '#000000');
    setShapeBorderColor(style.color ?? style.fillColor ?? '#000000');
    if (textMode) {
      setShapeColor(feature.properties.color);
      setShapeBorderColor(feature.properties.color);
    }
    if (circleMarkerMode) {
      setShapeBorderColor(feature.properties.color);
    }
    const initialOpacity = circleMarkerMode
      ? style.opacity ?? style.fillOpacity ?? 1
      : geom.type === 'LineString'
      ? style.opacity ?? style.fillOpacity ?? 0.2
      : style.fillOpacity ?? 0.2;

    setShapeOpacity(initialOpacity);
    setOpacityInput(Math.round(initialOpacity * 100).toString());

    // manual input panel values for LineString/Polygon
    if (geom.type === 'Point') {
      setInputs({});
    } else {
      const arr = geom.type === 'Polygon' ? (geom.coordinates as number[][][])[0] : (geom.coordinates as number[][]);
      const rec: Record<number, { lat: string; lng: string }> = {};
      arr.forEach(([lng, lat], i) => {
        rec[i] = {
          lat: lat === 0 ? '0' : lat.toFixed(6),
          lng: lng === 0 ? '0' : lng.toFixed(6),
        };
      });
      setInputs(rec);
    }

    // one time center point
    if (geom.type === 'Point') {
      // rotate around the point itself
      const [lng, lat] = geom.coordinates as [number, number];
      centerRef.current = { lng, lat };
    } else if (geom.type === 'LineString') {
      // line
      const line = geom.coordinates as number[][];
      centerRef.current = calculateCenter(line);
    } else if (geom.type === 'Polygon') {
      // polygon
      const ring = (geom.coordinates as number[][][])[0];
      centerRef.current = calculateCenter([ring]);
    } else {
      centerRef.current = { lng: 0, lat: 0 };
    }

    setRotation(0);
    setIsEditingKm(false);
    form.resetFields();
  };

  useEffect(() => {
    initializeState();
  }, [feature]);

  // Start point marker for Polygons
  useEffect(() => {
    if (!map || !feature || featureType !== 'Polygon' || isCircleWithRadius) return;

    // remove any existing start marker
    if (startPointMarker) {
      map.removeLayer(startPointMarker);
      setStartPointMarker(null);
    }

    // don't draw until all coords are valid
    if (coordinates.some(([lng, lat]) => Number.isNaN(lng) || Number.isNaN(lat))) {
      return;
    }

    // rotate coords
    if (!centerRef.current) return;
    const { lng: cx, lat: cy } = centerRef.current;
    const angleRad = (rotation * Math.PI) / 180;
    const rotated = coordinates.map(([lng, lat]) => rotatePoint(lng, lat, cx, cy, angleRad));

    const [startLng, startLat] = rotated[0]!;
    if (startLng == null || startLat == null) return;

    const marker = L.circleMarker([startLat, startLng], {
      radius: 6,
      color: 'white',
      fillColor: '#007bff',
      fillOpacity: 1,
      weight: 1.5,
    })
      .addTo(map)
      .bindTooltip('Start-End Point', { permanent: false, direction: 'top', offset: [0, -10] });

    setStartPointMarker(marker);

    return () => {
      map.removeLayer(marker);
      setStartPointMarker(null);
    };
  }, [map, feature, featureType, coordinates, rotation, isCircleWithRadius]);

  // Update manual inputs when slider or rotation changes
  const updateInputsFromRotation = (angle: number) => {
    if (!featureType || !coordinates.length) return;
    const baseCoords = featureType === 'Polygon' ? [coordinates] : coordinates;
    const { lng: cx, lat: cy } = centerRef.current || { lng: 0, lat: 0 };
    const angleRad = (angle * Math.PI) / 180;
    const rotated =
      featureType === 'Polygon'
        ? (baseCoords as number[][][])[0].map(([lng, lat]) => rotatePoint(lng, lat, cx, cy, angleRad))
        : (baseCoords as number[][]).map(([lng, lat]) => rotatePoint(lng, lat, cx, cy, angleRad));
    const rec: Record<number, { lat: string; lng: string }> = {};
    rotated.forEach(([lng, lat], i) => {
      rec[i] = {
        lat: lat === 0 ? '0' : lat.toFixed(6),
        lng: lng === 0 ? '0' : lng.toFixed(6),
      };
    });
    setInputs(rec);
  };

  // Slider / input handlers
  const handleSliderChange = (val: number) => {
    setRotation(val);
    updateInputsFromRotation(val);
  };
  const handleRotationInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    let v = parseInt(e.target.value, 10);
    if (Number.isNaN(v)) v = 0;
    v = Math.max(0, Math.min(360, v));
    setRotation(v);
    updateInputsFromRotation(v);
  };

  // Manual coordinate edits
  const updateCoordinate = (idx: number, pos: 0 | 1, val: number | null) => {
    if (val == null || Number.isNaN(val)) return;
    const clamped = pos === 1 ? Math.max(-90, Math.min(90, val)) : Math.max(-180, Math.min(180, val));
    const cpy = [...coordinates];
    cpy[idx][pos] = clamped;
    if (featureType === 'Polygon' && idx === 0 && cpy.length > 1) {
      cpy[cpy.length - 1][pos] = clamped;
    }
    setCoordinates(cpy);
  };

  // add / delete edges
  const addNewEdge = () => {
    const cpy = [...coordinates];
    let newIdx: number;

    if (featureType === 'Polygon') {
      cpy.pop();
      cpy.push([NaN, NaN], cpy[0]);
      newIdx = cpy.length - 2;
    } else {
      newIdx = cpy.length;
      cpy.push([NaN, NaN]);
    }

    setCoordinates(cpy);

    setInputs((prev) => ({
      ...prev,
      [newIdx]: { lat: '', lng: '' },
    }));
  };

  const deleteEdge = (i: number) => {
    const cpy = [...coordinates];
    cpy.splice(i, 1);
    setCoordinates(cpy);
  };

  const clickAddPoint = () => setIsSelecting((b) => !b);

  useEffect(() => {
    if (!map || !featureType) return;
    // const baseCoords = featureType === 'Polygon' ? [coordinates] : coordinates;
    const { lng: cx, lat: cy } = centerRef.current || { lng: 0, lat: 0 };
    const angleRad = (rotation * Math.PI) / 180;

    const handler = (e: L.LeafletMouseEvent) => {
      const [rawLng, rawLat] = inverseRotatePoint(e.latlng.lng, e.latlng.lat, cx, cy, angleRad);

      // append it:
      const copy = [...coordinates];
      let newIdx: number;
      if (featureType === 'Polygon') {
        copy.pop();
        copy.push([rawLng, rawLat], copy[0]!);
        newIdx = copy.length - 2;
      } else {
        newIdx = copy.length;
        copy.push([rawLng, rawLat]);
      }
      setCoordinates(copy);

      setInputs((prev) => ({
        ...prev,
        [newIdx]: { lat: rawLat.toFixed(6), lng: rawLng.toFixed(6) },
      }));
    };

    if (isSelecting) {
      map.getContainer().style.cursor = 'crosshair';
      map.on('click', handler);
    } else {
      map.getContainer().style.cursor = '';
    }

    return () => {
      map.off('click', handler);
    };
  }, [isSelecting, map, coordinates, rotation, featureType]);

  // Preview rotate-manual editing
  useEffect(() => {
    if (!map || !feature || (isCircleWithRadius && featureType === 'Point')) {
      return;
    }
    // select the “real” points (for Polygon we ignore the closing duplicate)
    const pts = featureType === 'Polygon' ? coordinates.slice(0, -1) : coordinates;
    // need at least 2 for LineString or 3 for Polygon
    const minPts = featureType === 'Polygon' ? 3 : 2;
    if (pts.length < minPts) {
      // remove stale preview
      if (previewLayerRef.current) {
        map.removeLayer(previewLayerRef.current);
        previewLayerRef.current = null;
      }
      return;
    }

    // skip until every manual input is non-empty
    const missing = pts.some((_, i) => {
      const inp = inputs[i];
      return !inp || inp.lat.trim() === '' || inp.lng.trim() === '';
    });
    if (missing) {
      if (previewLayerRef.current) {
        map.removeLayer(previewLayerRef.current);
        previewLayerRef.current = null;
      }
      return;
    }

    // skip if any coordinate is NaN
    if (pts.some(([lng, lat]) => Number.isNaN(lng) || Number.isNaN(lat))) {
      if (previewLayerRef.current) {
        map.removeLayer(previewLayerRef.current);
        previewLayerRef.current = null;
      }
      return;
    }

    // compute rotated coords
    const baseCoords = featureType === 'Polygon' ? ([coordinates] as number[][][]) : (coordinates as number[][]);
    const { lng: cx, lat: cy } = centerRef.current || { lng: 0, lat: 0 };
    const angleRad = (rotation * Math.PI) / 180;
    const rotated =
      featureType === 'Polygon'
        ? (baseCoords as number[][][])[0].map(([lng, lat]) => rotatePoint(lng, lat, cx, cy, angleRad))
        : (baseCoords as number[][]).map(([lng, lat]) => rotatePoint(lng, lat, cx, cy, angleRad));

    // build a GeoJSON feature
    const previewGeo: GeoJSON.Feature = {
      type: 'Feature',
      properties: feature.properties || {},
      geometry: {
        type: featureType!,
        coordinates: featureType === 'Polygon' ? [rotated] : rotated,
      } as any,
    };

    // clear old preview
    if (previewLayerRef.current) {
      map.removeLayer(previewLayerRef.current);
      previewLayerRef.current = null;
    }

    // add new preview layer
    const layer = L.geoJSON(previewGeo, {
      style: {
        color: shapeBorderColor,
        weight: 3,
        opacity: shapeOpacity,
        dashArray: isDashed ? '5,10' : undefined,
        fillColor: shapeColor,
        fillOpacity: shapeOpacity,
      },
    }).addTo(map);

    previewLayerRef.current = layer;

    return () => {
      map.removeLayer(layer);
      previewLayerRef.current = null;
    };
  }, [
    map,
    feature,
    featureType,
    coordinates,
    rotation,
    shapeBorderColor,
    shapeColor,
    shapeOpacity,
    isDashed,
    isCircleWithRadius,
    inputs,
  ]);

  useEffect(() => {
    if (!map || !feature || !isCircleMarker) return;

    // cleanup any old marker
    if (tempCircleLayer) {
      map.removeLayer(tempCircleLayer);
      setTempCircleLayer(null);
    }

    // pull out coords
    const [lng, lat] = feature.geometry.coordinates as [number, number];
    // Raptiye SVG
    // const svg = `<svg width="30px" height="30px" viewBox="0 0 16 16" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
    // <path fill="${shapeBorderColor}" fill-opacity="${shapeOpacity}" d="M11 6.5v-5.5h1v-1h-8v1h1v5.5c0 0-2 1.5-2 3.5 0 0.5 1.9 0.7 4 0.7v2.2c0 0.7 0.2 1.4 0.5 2.1l0.5 1 0.5-1c0.3-0.6 0.5-1.3 0.5-2.1v-2.2c2.1 0 4-0.3 4-0.7 0-2-2-3.5-2-3.5zM7 6.6c0 0-0.5 0.3-1.6 1.4-1 1-1.5 1.9-1.5 1.9s0.1-1 0.8-1.9c0.9-1.1 1.3-1.4 1.3-1.4v-5.6h1v5.6z"></path>
    // </svg>`;
    // Bayrak svg
    // const svg = `<svg width="30" height="30" viewBox="0 0 0.75 0.75" xmlns="http://www.w3.org/2000/svg"><path d="M.356 0a.02.02 0 0 0-.019.019V.6a.019.019 0 1 0 .037 0V.243L.406.241l.077.002c.035.006.068.03.112.037C.62.284.648.278.673.273L.717.261A.02.02 0 0 0 .73.243V.055A.02.02 0 0 0 .705.037S.687.043.665.048.615.057.602.055C.57.05.536.025.49.018L.405.015C.388.015.383.016.374.017a.02.02 0 0 0-.019-.018m.093.054.035.002c.035.006.068.03.112.037C.62.096.648.09.673.085L.692.08v.149L.664.237a.2.2 0 0 1-.063.007C.569.239.535.214.489.207L.404.204C.387.204.382.205.373.206v-.15L.405.054h.042M.3.452a.7.7 0 0 0-.189.037.2.2 0 0 0-.078.045Q.002.562 0 .6c-.002.038.013.047.033.065q.03.027.078.045c.064.025.15.04.245.04S.537.735.601.71Q.65.692.679.665C.708.638.713.625.713.6S.7.553.68.535A.2.2 0 0 0 .602.49.6.6 0 0 0 .413.453V.49q.105.008.176.035.044.018.066.038c.022.02.02.025.02.037S.669.624.655.637.618.664.589.675C.531.698.448.712.357.712S.183.697.125.675Q.081.657.059.637C.037.617.037.612.037.6S.043.576.057.563.094.536.123.525A.6.6 0 0 1 .299.49z" style="fill:${shapeBorderColor};fill-opacity:${shapeOpacity};stroke:none;stroke-width:0"/></svg>`;
    const svg = `<svg width="30" height="30" viewBox="0 0 0.75 0.75" xmlns="http://www.w3.org/2000/svg"><path d="M.356 0a.02.02 0 0 0-.019.019V.6a.019.019 0 1 0 .037 0V.276L.662.168a.019.019 0 0 0 0-.035L.375.024V.019A.02.02 0 0 0 .356 0m.019.065L.603.15.375.235zM.3.452a.7.7 0 0 0-.189.037Q.062.507.033.534C.004.561 0 .575 0 .6s.013.047.033.065q.03.027.078.045c.064.025.15.04.245.04S.537.735.601.71Q.65.692.679.665C.708.638.713.625.713.6S.7.553.68.535A.2.2 0 0 0 .602.49.6.6 0 0 0 .413.453V.49q.105.008.176.035.044.018.066.038c.022.02.02.025.02.037S.669.624.655.637.618.664.589.675C.531.698.448.712.357.712S.183.697.125.675Q.081.657.059.637C.037.617.037.612.037.6S.043.576.057.563.094.536.123.525A.6.6 0 0 1 .299.49z" style="fill:${shapeBorderColor};fill-opacity:${shapeOpacity};stroke:none;stroke-width:0"/></svg>`;

    // add it as a divIcon marker
    const marker = L.marker([lat, lng], {
      icon: L.divIcon({
        className: 'draw-circlemarker-svg-icon',
        html: svg,
        iconSize: [30, 30],
        iconAnchor: [15, 30],
      }),
    }).addTo(map);

    setTempCircleLayer(marker as L.Marker);

    // cleanup on unmount / deps change
    return () => {
      map.removeLayer(marker);
      setTempCircleLayer(null);
    };
  }, [map, feature, isCircleMarker, shapeBorderColor, shapeOpacity]);

  // Circle preview
  useEffect(() => {
    if (!map || !feature || !isCircleWithRadius || circleRadius == null || feature.geometry.type !== 'Point') {
      if (tempCircleLayer) {
        map?.removeLayer(tempCircleLayer);
        setTempCircleLayer(null);
      }
      return;
    }
    const [lng, lat] = feature.geometry.coordinates as [number, number];
    if (tempCircleLayer) map.removeLayer(tempCircleLayer);

    const circle = L.circle([lat, lng], {
      radius: circleRadius,
      color: shapeBorderColor,
      fillColor: shapeColor,
      fillOpacity: shapeOpacity,
      weight: 2,
      dashArray: isDashed ? '5,10' : undefined,
    }).addTo(map);

    setTempCircleLayer(circle);
    return () => {
      map.removeLayer(circle);
      setTempCircleLayer(null);
    };
  }, [
    map,
    feature,
    isCircleWithRadius,
    circleRadius,
    featureType,
    shapeBorderColor,
    shapeColor,
    shapeOpacity,
    isDashed,
  ]);

  // Last‐point marker
  useEffect(() => {
    if (!map || !feature || isCircleWithRadius || !isSelecting) return;

    // remove old
    if (lastPointMarker) {
      map.removeLayer(lastPointMarker);
      setLastPointMarker(null);
    }
    if (!coordinates.length) return;

    if (coordinates.some(([lng, lat]) => Number.isNaN(lng) || Number.isNaN(lat))) {
      return;
    }

    // rotate coords
    const baseCoords = featureType === 'Polygon' ? [coordinates] : coordinates;
    const { lng: cx, lat: cy } = centerRef.current || { lng: 0, lat: 0 };
    const angleRad = (rotation * Math.PI) / 180;
    const rotated =
      featureType === 'Polygon'
        ? (baseCoords as number[][][])[0].map(([lng, lat]) => rotatePoint(lng, lat, cx, cy, angleRad))
        : (baseCoords as number[][]).map(([lng, lat]) => rotatePoint(lng, lat, cx, cy, angleRad));

    // pick last point
    const lastIndex = featureType === 'Polygon' ? Math.max(0, rotated.length - 2) : rotated.length - 1;
    const [lng, lat] = rotated[lastIndex];
    if (lng == null || lat == null) return;

    const pulseIcon = L.divIcon({
      className: styles.pulseContainer,
      html: `<div class="${styles.pulseMarker}"></div>`,
      iconSize: [30, 30],
      iconAnchor: [15, 15],
    });
    const marker = L.marker([lat, lng], { icon: pulseIcon, zIndexOffset: -1000 })
      .addTo(map)
      .bindTooltip('Last Point (click to apply)', {
        permanent: false,
        direction: 'top',
        offset: [0, -10],
      });

    // add this:
    marker.on('click', () => {
      marker.off('click');
      applyChanges();
    });

    setLastPointMarker(marker);

    return () => {
      map.removeLayer(marker);
      setLastPointMarker(null);
    };
  }, [map, feature, featureType, coordinates, rotation, isCircleWithRadius, isSelecting]);

  const onKmChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    if (/^[0-9]*[.,]?[0-9]*$/.test(v) || v === '') {
      setInputKm(v);
    }
  };

  const applyChanges = async () => {
    if (!feature || !map) return;

    if (!shapeName.trim()) {
      statusHelper.displayErrorMessage('Please enter a name for the shape.');
      return;
    }

    if (!textFontSize.toString().trim()) {
      statusHelper.displayErrorMessage('Please enter a font size for the text.');
      return;
    }

    [previewLayerRef.current, lastPointMarker, tempCircleLayer, startPointMarker].forEach((layer) => {
      if (layer) map.removeLayer(layer);
    });

    let newGeo: any;

    if (isCircleWithRadius && featureType === 'Point') {
      newGeo = {
        ...feature,
        properties: {
          ...feature.properties,
          radius: circleRadius!,
          style: {
            ...(feature.properties.style || {}),
            dashArray: isDashed ? '5,10' : undefined,
            color: shapeBorderColor,
            fillColor: shapeColor,
            fillOpacity: shapeOpacity,
          },
        },
      };
    } else if (isCircleMarker) {
      newGeo = {
        ...feature,
        properties: {
          ...feature.properties!,
          name: shapeName,
          text: shapeName,
          style: {
            ...(feature.properties!.style || {}),
            color: shapeBorderColor,
            fillColor: shapeColor,
            fillOpacity: shapeOpacity,
            opacity: shapeOpacity,
          },
        },
      };
    } else if (isMarker) {
      newGeo = {
        ...feature,
        properties: {
          ...feature.properties!,
          name: shapeName,
          text: shapeName,
        },
      };
    } else if (isText) {
      newGeo = {
        ...feature,
        properties: {
          ...feature.properties!,
          name: shapeName,
          text: shapeName,
          color: shapeBorderColor,
          fontSize: textFontSize,
        },
      };
    } else {
      const base = featureType === 'Polygon' ? [coordinates] : coordinates;
      const { lng: cx, lat: cy } = centerRef.current || { lng: 0, lat: 0 };
      const angleRad = (rotation * Math.PI) / 180;
      const finalCoords =
        featureType === 'Polygon'
          ? (base as number[][][]).map((ring) => ring.map(([lng, lat]) => rotatePoint(lng, lat, cx, cy, angleRad)))
          : (base as number[][]).map(([lng, lat]) => rotatePoint(lng, lat, cx, cy, angleRad));
      const coordsToStore = featureType === 'Polygon' ? [finalCoords[0]] : finalCoords;

      const {
        arrowHead: oldArrowHead,
        startEndArrow: oldStartEndArrow,
        style: oldStyle = {},
        ...otherProps
      } = feature.properties!;

      const styleProps = {
        ...oldStyle,
        dashArray: isDashed ? '5,10' : undefined,
        color: shapeBorderColor,
        fillColor: shapeColor,
        fillOpacity: shapeOpacity,
        ...(featureType === 'LineString' && { opacity: shapeOpacity, fillOpacity: shapeOpacity }),
      };

      let arrowProps: Record<string, any> = {};
      if (featureType === 'LineString') {
        if (arrowType === ArrowType.START_END_ARROW) {
          arrowProps = { startEndArrow: true };
        } else if (arrowType === ArrowType.ARROW_HEADS) {
          arrowProps = { arrowHead: { size: '10px', frequency: '60px' } };
        }
        arrowProps.arrowFillType = arrowFillType === ArrowFillType.NONE ? false : arrowFillType;
      }

      newGeo = {
        ...feature,
        geometry: {
          ...feature.geometry,
          coordinates: coordsToStore,
        },
        properties: {
          ...otherProps,
          style: styleProps,
          ...arrowProps,
        },
      };
    }

    map.eachLayer((layer) => {
      if ((layer as any).feature?.properties?.uuid === feature.properties?.uuid) {
        map.removeLayer(layer);
      }
    });

    await drawenLayerService.drawenLayerEdit({
      name: shapeName,
      uuid: feature.properties!.uuid,
      features: newGeo,
    });
    setIsSelecting(false);
    if (map) {
      map.getContainer().style.cursor = '';
    }
    onClose();
  };

  // Close handler resets everything
  const handleClose = () => {
    if (previewLayerRef.current) {
      map?.removeLayer(previewLayerRef.current);
      previewLayerRef.current = null;
    }
    if (lastPointMarker) {
      map?.removeLayer(lastPointMarker);
      setLastPointMarker(null);
    }
    if (tempCircleLayer) {
      map?.removeLayer(tempCircleLayer);
      setTempCircleLayer(null);
    }
    if (startPointMarker) {
      map?.removeLayer(startPointMarker);
      setStartPointMarker(null);
    }
    setIsSelecting(false);
    if (map) {
      map.getContainer().style.cursor = '';
    }

    initializeState();
    onClose();
  };

  const renderRotateView = () => (
    <>
      <p className="popup-input-title">Adjust Rotation (Degrees)</p>
      <div className="slider-container">
        <Slider min={0} max={360} step={10} value={rotation} onChange={handleSliderChange} style={{ flex: 1 }} />
        <Input
          type="number"
          value={rotation}
          onChange={handleRotationInput}
          min={0}
          max={360}
          step={10}
          style={{ marginLeft: 10, width: 80 }}
          suffix="°"
        />
      </div>
    </>
  );

  const renderManualEditingView = () => {
    const pts = featureType === 'Polygon' ? coordinates.slice(0, -1) : coordinates;
    return (
      <div className={styles.coordinatesContainer}>
        <div className={styles.pointContainer}>
          {pts.map((pt, i) => (
            <div key={i} className={styles.pointInput}>
              <span className={styles.edgeText}>{i + 1}:</span>
              <Tooltip title="Acceptable range: -90° to 90°." zIndex={12000}>
                <Input
                  value={inputs[i]?.lat ?? ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    const key = `lat-${i}`;
                    if (/^-?\d*\.?\d*$/.test(v) || v === '') {
                      setInputs((old) => ({ ...old, [i]: { ...(old[i] || {}), lat: v } }));
                      const n = parseFloat(v);
                      if (v === '') updateCoordinate(i, 1, null);
                      else if (!Number.isNaN(n)) {
                        updateCoordinate(i, 1, n);
                        if (correctionTimeoutsRef.current[key]) clearTimeout(correctionTimeoutsRef.current[key]);
                        correctionTimeoutsRef.current[key] = setTimeout(() => {
                          if (n < -90 || n > 90) {
                            const c = Math.max(-90, Math.min(90, n));
                            updateCoordinate(i, 1, c);
                            setInputs((old) => ({ ...old, [i]: { ...(old[i] || {}), lat: c.toString() } }));
                          }
                        }, 500);
                      }
                    }
                  }}
                  addonAfter="Lat"
                />
              </Tooltip>
              <Tooltip title="Acceptable range: -180° to 180°." zIndex={12000}>
                <Input
                  value={inputs[i]?.lng ?? ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    const key = `lng-${i}`;
                    if (/^-?\d*\.?\d*$/.test(v) || v === '') {
                      setInputs((old) => ({ ...old, [i]: { ...(old[i] || {}), lng: v } }));
                      const n = parseFloat(v);
                      if (v === '') updateCoordinate(i, 0, null);
                      else if (!Number.isNaN(n)) {
                        updateCoordinate(i, 0, n);
                        if (correctionTimeoutsRef.current[key]) clearTimeout(correctionTimeoutsRef.current[key]);
                        correctionTimeoutsRef.current[key] = setTimeout(() => {
                          if (n < -180 || n > 180) {
                            const c = Math.max(-180, Math.min(180, n));
                            updateCoordinate(i, 0, c);
                            setInputs((old) => ({ ...old, [i]: { ...(old[i] || {}), lng: c.toString() } }));
                          }
                        }, 500);
                      }
                    }
                  }}
                  addonAfter="Lng"
                />
              </Tooltip>
              {pts.length > (featureType === 'Polygon' ? 4 : 2) && (
                <Popconfirm title="Delete this point?" onConfirm={() => deleteEdge(i)} okText="Yes" cancelText="No">
                  <Button
                    icon={<DeleteOutlined />}
                    danger
                    style={{ marginLeft: '5px', color: '#ff4d4f', backgroundColor: 'transparent' }}
                  />
                </Popconfirm>
              )}
            </div>
          ))}
        </div>
        <div className={styles.addPointContainer}>
          <Button
            icon={<SelectOutlined />}
            type={isSelecting ? 'primary' : 'default'}
            danger={isSelecting}
            onClick={clickAddPoint}
          >
            {isSelecting ? 'Stop Selecting' : 'Select on Map'}
          </Button>
          <Button icon={<AppstoreAddOutlined />} onClick={addNewEdge}>
            Add New Point
          </Button>
        </div>
      </div>
    );
  };

  const renderCircleRadiusView = () => (
    <div className={styles.radiusControl}>
      <p className="popup-input-title">Adjust Circle Radius (kilometers)</p>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <Button icon={<MinusOutlined />} onClick={() => setCircleRadius((r) => Math.max(1, (r || 0) - radiusStep))} />
        <Input
          value={inputKm}
          onFocus={() => setIsEditingKm(true)}
          onChange={onKmChange}
          onBlur={() => {
            setIsEditingKm(false);
            const norm = inputKm.replace(',', '.');
            const km = parseFloat(norm);
            if (!Number.isNaN(km)) setCircleRadius(km * 1000);
            else if (circleRadius != null) setInputKm(formatKm(circleRadius / 1000));
          }}
          addonAfter="km"
        />
        <Button icon={<PlusOutlined />} onClick={() => setCircleRadius((r) => (r || 0) + radiusStep)} />
      </div>
      <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center', gap: '10px' }}>
        <p style={{ width: '150px', alignSelf: 'center', marginTop: '15px' }} className="popup-input-title">
          Step Size
        </p>
        <Select
          className={styles.selectbox}
          value={radiusStep}
          onChange={setRadiusStep}
          options={[
            { value: 100, label: '100 m' },
            { value: 200, label: '200 m' },
            { value: 500, label: '500 m' },
            { value: 1000, label: '1 km' },
            { value: 2000, label: '2 km' },
            { value: 5000, label: '5 km' },
            { value: 10000, label: '10 km' },
            { value: 20000, label: '20 km' },
            { value: 50000, label: '50 km' },
            { value: 100000, label: '100 km' },
            { value: 200000, label: '200 km' },
            { value: 500000, label: '500 km' },
          ]}
        />
      </div>
      <hr style={{ margin: '16px 0' }} />
      <p className="popup-input-title">Shape Name</p>
      <Input value={shapeName} onChange={(e) => setShapeName(e.target.value)} placeholder="Enter name" />
      <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span className="popup-input-title">Dashed Line</span>
        <Switch
          className="custom-switch"
          checked={isDashed}
          onChange={setIsDashed}
          checkedChildren="Yes"
          unCheckedChildren="No"
        />
      </div>
      <div style={{ marginTop: 16, display: 'flex', gap: 20 }}>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ paddingTop: 10 }} className="popup-input-title">
            Fill Color
          </span>
          <input
            type="color"
            value={shapeColor}
            onChange={(e) => setShapeColor(e.target.value)}
            style={{ width: '50px', height: '30px', border: 'none', cursor: 'pointer', backgroundColor: '#333333' }}
          />
        </div>
        <div style={{ width: 1, backgroundColor: '#ccc', margin: '0 10px' }} />
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ paddingTop: 10 }} className="popup-input-title">
            Border Color
          </span>
          <input
            type="color"
            value={shapeBorderColor}
            onChange={(e) => setShapeBorderColor(e.target.value)}
            style={{ width: '50px', height: '30px', border: 'none', cursor: 'pointer', backgroundColor: '#333333' }}
          />
        </div>
      </div>
      <div className="slider-container" style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: '0.5rem' }}>
        <span style={{ paddingTop: 10 }} className="popup-input-title">
          Opacity
        </span>
        <Slider
          min={0}
          max={100}
          value={Math.round(shapeOpacity * 100)}
          onChange={(pct) => {
            setShapeOpacity(pct / 100);
            setOpacityInput(pct.toString());
          }}
          style={{ flex: 1 }}
        />
        <Input
          style={{ width: 70 }}
          value={opacityInput}
          suffix="%"
          onChange={(e) => {
            const v = e.target.value;
            if (/^\d{0,3}$/.test(v)) {
              setOpacityInput(v);
              const n = parseInt(v, 10);
              if (!Number.isNaN(n)) setShapeOpacity(n / 100);
            }
          }}
          onBlur={() => {
            let n = parseInt(opacityInput, 10);
            if (Number.isNaN(n)) n = 0;
            n = Math.min(100, Math.max(0, n));
            setOpacityInput(n.toString());
            setShapeOpacity(n / 100);
          }}
        />
      </div>
    </div>
  );

  const renderCircleMarkerView = () => (
    <div>
      <p className="popup-input-title">Marker Name</p>
      <Input
        value={shapeName}
        onChange={(e) => setShapeName(e.target.value)}
        placeholder="Enter name"
        style={{ marginBottom: 16 }}
      />
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ paddingTop: 10 }} className="popup-input-title">
          Color
        </span>
        <input
          type="color"
          value={shapeBorderColor}
          onChange={(e) => setShapeBorderColor(e.target.value)}
          style={{ width: '50px', height: '30px', border: 'none', cursor: 'pointer', backgroundColor: '#333333' }}
        />
      </div>

      {/* <p className="popup-input-title">Opacity</p>
      <div className="slider-container" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <Slider
          min={1}
          max={100}
          value={Math.round(shapeOpacity * 100)}
          onChange={(pct) => {
            setShapeOpacity(pct / 100);
            setOpacityInput(pct.toString());
          }}
          style={{ flex: 1 }}
        />
        <Input
          style={{ width: 70 }}
          suffix="%"
          value={opacityInput}
          onChange={(e) => {
            const v = e.target.value;
            if (/^\d{0,3}$/.test(v)) {
              setOpacityInput(v);
              const n = parseInt(v, 10);
              if (!Number.isNaN(n)) setShapeOpacity(n / 100);
            }
          }}
          onBlur={() => {
            let n = parseInt(opacityInput, 10);
            if (Number.isNaN(n)) n = 0;
            n = Math.min(100, Math.max(0, n));
            setOpacityInput(n.toString());
            setShapeOpacity(n / 100);
          }}
        />
      </div> */}
    </div>
  );

  const renderMarkerView = () => (
    <div>
      <p className="popup-input-title">Marker Name</p>
      <Input
        value={shapeName}
        onChange={(e) => setShapeName(e.target.value)}
        placeholder="Enter name"
        style={{ marginBottom: 16 }}
      />
    </div>
  );

  const renderTextView = () => (
    <div>
      <p className="popup-input-title">Text Name</p>
      <Input
        value={shapeName}
        onChange={(e) => setShapeName(e.target.value)}
        placeholder="Enter name"
        style={{ marginBottom: 16 }}
      />

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            flex: 7,
          }}
        >
          <p className="popup-input-title" style={{ marginRight: 8, whiteSpace: 'nowrap' }}>
            Font Size:
          </p>
          <Input
            type="number"
            min={1}
            max={100}
            value={textFontSize}
            onChange={(e) => setTextFontSize(Number(e.target.value))}
            addonAfter="px"
          />
        </div>

        {/* Divider */}
        <div
          style={{
            width: 1,
            backgroundColor: '#ccc',
            margin: '0 16px',
            height: '32px',
          }}
        />

        {/* Color picker group (30%) */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            flex: 3,
          }}
        >
          <p className="popup-input-title" style={{ marginRight: 8, whiteSpace: 'nowrap' }}>
            Color:
          </p>
          <input
            type="color"
            value={shapeBorderColor}
            onChange={(e) => setShapeBorderColor(e.target.value)}
            style={{ width: '50px', height: '30px', border: 'none', cursor: 'pointer', backgroundColor: '#333333' }}
          />
        </div>
      </div>
    </div>
  );

  return (
    <DraggableModal
      id="rotate-modal"
      headerText={`Edit Feature - ${groupName} `}
      className={styles.drag}
      position={{ x: 600, y: 100 }}
      contentClassName={styles.dragContent}
      onClickClose={handleClose}
      isDrawModal
    >
      <Form form={form}>
        {isCircleMarker ? (
          renderCircleMarkerView()
        ) : featureType === 'Point' && isCircleWithRadius && !isCircleMarker ? (
          renderCircleRadiusView()
        ) : isMarker ? (
          renderMarkerView()
        ) : isText ? (
          renderTextView()
        ) : (
          <>
            {renderRotateView()}
            <hr style={{ margin: '16px 0' }} />
            {renderManualEditingView()}
            <hr style={{ margin: '16px 0' }} />
            <p className="popup-input-title">Shape Name</p>
            <Input value={shapeName} onChange={(e) => setShapeName(e.target.value)} placeholder="Enter name" />

            {featureType !== 'Point' && (
              <div style={{ marginTop: 16, display: 'flex', justifyContent: 'space-between' }}>
                <span className="popup-input-title">Dashed Line</span>
                <Switch
                  className="custom-switch"
                  checked={isDashed}
                  onChange={setIsDashed}
                  checkedChildren="Yes"
                  unCheckedChildren="No"
                />
              </div>
            )}

            {featureType === 'LineString' && (
              <div>
                <div style={{ marginTop: 16, display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ paddingTop: 10 }} className="popup-input-title">
                    Arrow Style
                  </span>
                  <SearchableSelect
                    style={{ width: '50%', border: '1px solid #4f4f4f' }}
                    notSorted
                    value={arrowType}
                    onChange={(v) => setArrowType(v as ArrowType)}
                    allowClear={false}
                  >
                    <Select.Option value={ArrowType.NONE}>None</Select.Option>
                    <Select.Option value={ArrowType.ARROW_HEADS}>Multiple Arrows</Select.Option>
                    <Select.Option value={ArrowType.START_END_ARROW}>Start-End Arrows</Select.Option>
                  </SearchableSelect>
                </div>
                <div style={{ marginTop: 16, display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ paddingTop: 10 }} className="popup-input-title">
                    Arrow Fill Type
                  </span>
                  <SearchableSelect
                    style={{
                      width: '50%',
                      border: arrowType === ArrowType.NONE ? '1px solid rgba(79, 79, 79, 0.5)' : '1px solid #4f4f4f',
                    }}
                    notSorted
                    value={arrowType === ArrowType.NONE ? ArrowFillType.DISABLED : arrowFillType}
                    onChange={(v) => setArrowFillType(v as ArrowFillType)}
                    allowClear={false}
                    disabled={arrowType === ArrowType.NONE}
                  >
                    {arrowType === ArrowType.NONE && (
                      <Select.Option value={ArrowFillType.DISABLED} disabled>
                        Unavailable
                      </Select.Option>
                    )}
                    <Select.Option value={ArrowFillType.NONE}>Standard Arrow</Select.Option>
                    <Select.Option value={ArrowFillType.FILL}>Filled Arrow</Select.Option>
                  </SearchableSelect>
                </div>
              </div>
            )}

            <div style={{ marginTop: 16, display: 'flex', gap: 20 }}>
              {featureType !== 'LineString' && (
                <>
                  <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ paddingTop: 10 }} className="popup-input-title">
                      Fill Color
                    </span>
                    <input
                      type="color"
                      value={shapeColor}
                      onChange={(e) => setShapeColor(e.target.value)}
                      style={{
                        width: '50px',
                        height: '30px',
                        border: 'none',
                        cursor: 'pointer',
                        backgroundColor: '#333333',
                      }}
                    />
                  </div>
                  <div style={{ width: 1, backgroundColor: '#ccc', margin: '0 10px' }} />
                </>
              )}
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ paddingTop: 10 }} className="popup-input-title">
                  {featureType === 'LineString' ? 'Line Color' : 'Border Color'}
                </span>
                <input
                  type="color"
                  value={shapeBorderColor}
                  onChange={(e) => setShapeBorderColor(e.target.value)}
                  style={{
                    width: '50px',
                    height: '30px',
                    border: 'none',
                    cursor: 'pointer',
                    backgroundColor: '#333333',
                  }}
                />
              </div>
            </div>

            <div
              className="slider-container"
              style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: '0.5rem' }}
            >
              <span style={{ paddingTop: 10 }} className="popup-input-title">
                Opacity
              </span>
              <Slider
                min={0}
                max={100}
                value={Math.round(shapeOpacity * 100)}
                onChange={(pct) => {
                  setShapeOpacity(pct / 100);
                  setOpacityInput(pct.toString());
                }}
                style={{ flex: 1 }}
              />
              <Input
                style={{ width: 70 }}
                suffix="%"
                value={opacityInput}
                onChange={(e) => {
                  const v = e.target.value;
                  if (/^\d{0,3}$/.test(v)) {
                    setOpacityInput(v);
                    const n = parseInt(v, 10);
                    if (!Number.isNaN(n)) setShapeOpacity(n / 100);
                  }
                }}
                onBlur={() => {
                  let n = parseInt(opacityInput, 10);
                  if (Number.isNaN(n)) n = 0;
                  n = Math.min(100, Math.max(0, n));
                  setOpacityInput(n.toString());
                  setShapeOpacity(n / 100);
                }}
              />
            </div>
          </>
        )}

        <div style={{ marginTop: 24, display: 'flex', justifyContent: 'space-between' }}>
          <button type="button" className="modal-button" onClick={handleClose}>
            Cancel
          </button>
          <button className="next-button" type="button" onClick={applyChanges}>
            Apply Changes
          </button>
        </div>
      </Form>
    </DraggableModal>
  );
};

export default RotateModal;
